// Arma el libro de Excel de Reportes: una hoja de resumen con indicadores y tablas
// (con barras dentro de las celdas) y una hoja con el detalle línea por línea.
import { descargarLibroExcel, fechaExcel, letraColumna } from "./excel";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fechaCorta = (iso) => iso.split("-").reverse().join("/");
const nombreMesLargo = (clave) => {
  const [anio, mes] = clave.split("-");
  return `${MESES[Number(mes) - 1]} ${anio}`;
};
const ahora = () =>
  new Date().toLocaleString("es-NI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const c = (valor, estilo, formula) => ({ valor, estilo, formula });
const cebra = (estilo, i) => (i % 2 ? `${estilo}Cebra` : estilo);

// Tabla de 3 columnas: nombre, monto y porcentaje del total.
function bloque(titulo, etiqueta, datos) {
  const total = datos.reduce((suma, d) => suma + d.monto, 0);
  const filas = [
    [c(titulo, "seccion"), c("", "seccion"), c("", "seccion")],
    [c(etiqueta, "encabezado"), c("Monto C$", "encabezadoDerecha"), c("% del total", "encabezadoDerecha")],
  ];
  if (!datos.length) {
    filas.push([c("Sin datos en este período.", "nota")]);
    return { filas, filasDatos: 0 };
  }
  datos.forEach((d, i) =>
    filas.push([c(d.nombre, cebra("texto", i)), c(d.monto, cebra("monto", i)), c(total ? d.monto / total : 0, cebra("porcentaje", i))])
  );
  filas.push([c("Total", "totalTexto"), c(total, "totalMonto"), c(total ? 1 : 0, "totalPorcentaje")]);
  return { filas, filasDatos: datos.length };
}

function hojaResumen({ datos, desde, hasta, area, tiempo }) {
  const r = datos.resumen;
  const filas = [];
  const combinar = ["A1:H1", "B2:H2", "B3:H3", "C5:D5", "C6:D6", "G5:H5", "G6:H6"];
  const barras = [];

  const banda = Array.from({ length: 8 }, (_, i) => c(i === 0 ? "Reporte de Compras" : "", i === 0 ? "banda" : "bandaVacia"));
  filas[0] = { alto: 42, celdas: banda };
  filas[1] = { alto: 20, celdas: [null, c("Colegio Alemán DSM  ·  Gestión de Compras", "subtitulo")] };
  filas[2] = {
    celdas: [null, c(`Período: ${fechaCorta(desde)} al ${fechaCorta(hasta)}     ·     Área: ${area}     ·     Generado: ${ahora()}`, "nota")],
  };
  filas[3] = { alto: 10 };
  filas[4] = {
    alto: 22,
    celdas: [
      null,
      c("GASTO APROBADO (CON IVA)", "kpiEtiqueta"),
      c("SOLICITUDES CREADAS", "kpiEtiqueta"),
      c("", "kpiEtiqueta"),
      null,
      c("RECHAZADAS", "kpiEtiqueta"),
      c("TIEMPO PROMEDIO DE APROBACIÓN", "kpiEtiqueta"),
      c("", "kpiEtiqueta"),
    ],
  };
  filas[5] = {
    alto: 34,
    celdas: [
      null,
      c(r.gasto_nio, "kpiValor"),
      c(r.solicitudes, "kpiNumero"),
      c("", "kpiNumero"),
      null,
      c(r.rechazadas, "kpiNumero"),
      c(tiempo, "kpiNumero"),
      c("", "kpiNumero"),
    ],
  };

  const pares = [
    [
      bloque("Gasto por mes", "Mes", datos.por_mes.map((d) => ({ ...d, nombre: nombreMesLargo(d.nombre) }))),
      bloque("Contado y crédito", "Condición", datos.por_condicion),
    ],
    [bloque("Gasto por área", "Área", datos.por_area), bloque("Gasto por proveedor", "Proveedor", datos.por_proveedor)],
    [bloque("Productos con más gasto", "Producto", datos.por_producto), null],
  ];

  let fila = 8; // número de fila de Excel donde empieza cada par de tablas
  pares.forEach((par) => {
    par.forEach((b, lado) => {
      if (!b) return;
      const col = lado === 0 ? 1 : 5;
      b.filas.forEach((celdas, i) => {
        const indice = fila - 1 + i;
        filas[indice] = filas[indice] || { celdas: [] };
        filas[indice].celdas = filas[indice].celdas || [];
        celdas.forEach((celda, j) => {
          filas[indice].celdas[col + j] = celda;
        });
      });
      filas[fila - 1].alto = 24;
      combinar.push(`${letraColumna(col)}${fila}:${letraColumna(col + 2)}${fila}`);
      if (b.filasDatos) {
        const montos = letraColumna(col + 1);
        barras.push(`${montos}${fila + 2}:${montos}${fila + 1 + b.filasDatos}`);
      }
    });
    fila += Math.max(...par.filter(Boolean).map((b) => b.filas.length)) + 2;
  });

  // Completa huecos para que el arreglo no tenga posiciones vacías.
  for (let i = 0; i < filas.length; i++) filas[i] = filas[i] || {};

  return {
    nombre: "Resumen",
    anchos: [2, 34, 18, 13, 3, 34, 18, 13],
    filas,
    combinar,
    barras,
    sinCuadricula: true,
  };
}

function hojaDetalle({ datos, desde, hasta, area }) {
  const encabezados = ["Solicitud", "Fecha aprob.", "Área", "Proveedor", "Producto", "Condición", "Moneda", "Monto C$ (con IVA)"];
  const filaEncabezado = 4;
  const primera = filaEncabezado + 1;
  const ultima = filaEncabezado + datos.detalle.length;

  const filas = [
    { alto: 36, celdas: encabezados.map((_, i) => c(i === 0 ? "Detalle de compras aprobadas" : "", i === 0 ? "banda" : "bandaVacia")) },
    { celdas: [c(`Período: ${fechaCorta(desde)} al ${fechaCorta(hasta)}     ·     Área: ${area}`, "nota")] },
    { alto: 8 },
    {
      alto: 22,
      celdas: encabezados.map((t, i) => c(t, i === encabezados.length - 1 ? "encabezadoDerecha" : "encabezado")),
    },
    ...datos.detalle.map((d, i) => ({
      celdas: [
        c(`#${String(d.solicitud_id).padStart(3, "0")}`, cebra("centro", i)),
        c(fechaExcel(d.fecha), cebra("fecha", i)),
        c(d.area || "Sin definir", cebra("texto", i)),
        c(d.proveedor, cebra("texto", i)),
        c(d.producto, cebra("texto", i)),
        c(d.condicion, cebra("centro", i)),
        c(d.moneda, cebra("centro", i)),
        c(d.monto_nio, cebra("monto", i)),
      ],
    })),
    {
      alto: 20,
      celdas: [
        c(`Total (${datos.detalle.length} ${datos.detalle.length === 1 ? "línea" : "líneas"})`, "totalTexto"),
        ...Array.from({ length: 6 }, () => c("", "totalTexto")),
        c(datos.resumen.gasto_nio, "totalMonto", `SUM(H${primera}:H${ultima})`),
      ],
    },
  ];

  return {
    nombre: "Detalle",
    anchos: [11, 13, 22, 28, 40, 12, 10, 20],
    filas,
    combinar: ["A1:H1", "A2:H2", `A${ultima + 1}:G${ultima + 1}`],
    congelar: filaEncabezado,
    filtro: `A${filaEncabezado}:H${ultima}`,
  };
}

export function descargarReporteExcel({ datos, desde, hasta, area, tiempo }) {
  const contexto = { datos, desde, hasta, area, tiempo };
  descargarLibroExcel({
    nombreArchivo: `reporte-compras-${desde}-a-${hasta}.xlsx`,
    hojas: [hojaResumen(contexto), hojaDetalle(contexto)],
  });
}
